/**
 * formUtils.js
 * Pure helper functions for the Form component.
 * No React, no context, no side effects — just data transformations.
 */

import countryList from "react-select-country-list";

const countries = countryList().getData();

/**
 * Converts a Date object to a YYYY-MM-DD string using local time.
 * Avoids timezone shift bugs from toISOString().
 */
export const dateToLocalString = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Converts a YYYY-MM-DD string back to a local Date object.
 * Safe against timezone shifts.
 */
export const stringToDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a YYYY-MM-DD string into a human-readable display date.
 * e.g. "2025-06-15" → "Sun, Jun 15"
 */
export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

/**
 * Returns default pickup (today) and dropoff (today + 2 days) as YYYY-MM-DD strings.
 */
export const getDefaultDates = () => {
  const today = new Date();
  const dropoff = new Date(today);
  dropoff.setDate(today.getDate() + 2);
  return {
    pickup: dateToLocalString(today),
    dropoff: dateToLocalString(dropoff),
  };
};

/**
 * Returns the default driver age range based on country code.
 * North America (US, CA, MX) → "26-70", everywhere else → "30-65"
 */
export const getDefaultAgeRange = (countryCode) => {
  const northAmerica = ["US", "CA", "MX"];
  return northAmerica.includes(countryCode) ? "26-70" : "30-65";
};

/**
 * Returns the country display name for a given ISO 3166-1 alpha-2 code.
 * Falls back to the code itself if not found.
 */
export const getCountryName = (code) => {
  if (!code) return "";
  const match = countries.find((c) => c.value === code);
  return match ? match.label : code;
};

export const formatRegionalTime = (timeStr, countryCode) => {
  if (!timeStr) return "";
  const TWENTYFOUR_HOUR_COUNTRIES = [
    "GB","DE","FR","ES","IT","NL","BE","AT","CH","SE","NO","DK","FI",
    "PL","CZ","HU","RO","GR","PT","IE","IN","CN","JP","KR","BR","MX",
    "AR","CL","CO","ZA","NG","EG","SA","AE","IL","TR","PK","BD","TH",
    "VN","ID","PH","MY","SG","AU","NZ",
  ];
  const use24h = TWENTYFOUR_HOUR_COUNTRIES.includes(countryCode);
  if (use24h) return timeStr;
  const [hours, minutes] = timeStr.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};
